import { ShieldAlert, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { WpPublishResult } from "./use-studio";

export interface WpPlanView {
  result: WpPublishResult;
  /** The server refused the publish (for example the sandbox gate) but still sent the plan. */
  blocked?: boolean;
}

interface PlanRow {
  label: string;
  value: string;
  tone?: "good" | "bad";
}

/**
 * Turns the server's plan into labelled rows. The plan is a short bulleted text block today
 * ("• title: ...", "• body: 36499 chars of HTML ...", "• would POST to: ...") and may become an object later;
 * both are handled, and anything not recognised is still shown as a plain line.
 */
export function parseWpPlan(plan: unknown): { rows: PlanRow[]; extra: string[]; raw: string } {
  const rows: PlanRow[] = [];
  const extra: string[] = [];

  if (plan && typeof plan === "object" && !Array.isArray(plan)) {
    for (const [k, v] of Object.entries(plan as Record<string, unknown>)) {
      if (v === null || v === undefined || typeof v === "object") continue;
      if (/^credentials?configured$/i.test(k)) {
        rows.push({ label: "WordPress login", value: v === true || v === "yes" ? "Set up" : "Not set up. Add it in Settings first.", tone: v === true || v === "yes" ? "good" : "bad" });
        continue;
      }
      rows.push({ label: k.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase()), value: String(v) });
    }
    return { rows, extra, raw: JSON.stringify(plan, null, 2) };
  }

  const raw = typeof plan === "string" ? plan : "";
  for (const line of raw.split(/\r?\n/)) {
    const t = line.replace(/^\s*[•*-]\s*/, "").trim();
    if (!t || /^\[(dry run|sandbox|live)\]/i.test(t) && !/blocked|aborted/i.test(t)) continue;
    let m: RegExpMatchArray | null;
    if ((m = t.match(/^title:\s*(.+)$/i))) rows.push({ label: "Title", value: m[1] });
    else if ((m = t.match(/^body:\s*([\d,]+)\s*chars?\s*of\s*HTML/i))) {
      rows.push({ label: "Article text", value: `${Number(m[1].replace(/,/g, "")).toLocaleString()} characters of formatted text, posted exactly as you see it` });
    } else if ((m = t.match(/^WordPress credentials configured:\s*(\w+)/i))) {
      const yes = /^y/i.test(m[1]);
      rows.push({ label: "WordPress login", value: yes ? "Set up" : "Not set up. Add it in Settings first.", tone: yes ? "good" : "bad" });
    } else if ((m = t.match(/^would POST to:\s*(\S+)\s*(?:\(status=(\w+)\))?/i))) {
      let where = m[1];
      try {
        where = new URL(m[1]).host;
      } catch { /* keep the raw text */ }
      rows.push({ label: "Would go to", value: where });
      if (m[2]) rows.push({ label: "Would be saved as", value: /^publish/i.test(m[2]) ? "Published right away (visible to everyone)" : m[2] });
    } else if (/^NO network call made/i.test(t)) {
      continue; // the dialog already says nothing was posted
    } else {
      extra.push(t);
    }
  }
  return { rows, extra, raw };
}

function reasonText(view: WpPlanView): { title: string; body: string; blocked: boolean } {
  const { result, blocked } = view;
  if (blocked || result.blocked) {
    return {
      title: "Publishing is blocked right now",
      body: result.message || "The server refused to post this article. Nothing was published.",
      blocked: true,
    };
  }
  if (result.dryRunReason === "role") {
    return {
      title: "Preview only: nothing was published",
      body: "Only the workspace owner or an admin can publish to the website, so this is what would be posted. Ask the owner to publish it.",
      blocked: false,
    };
  }
  if (result.dryRunReason === "requested") {
    return { title: "Preview: nothing was published", body: "This is what would be posted to your website if you publish the article.", blocked: false };
  }
  return { title: "Preview: nothing was published", body: "This is what would be posted to your website.", blocked: false };
}

export function WpPlanDialog({
  open,
  onOpenChange,
  view,
  articleTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: WpPlanView | null;
  articleTitle: string;
}) {
  if (!view) return null;
  const reason = reasonText(view);
  const { rows, extra, raw } = parseWpPlan(view.result.plan);
  const hasTitleRow = rows.some((r) => r.label === "Title");
  const loginMissing = view.result.credentialsConfigured === false && !rows.some((r) => r.label === "WordPress login");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] w-[calc(100vw-2rem)] max-w-xl overflow-y-auto" data-testid="dialog-wp-plan">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            {reason.blocked ? <ShieldAlert size={18} className="shrink-0 text-red-400" /> : <Eye size={18} className="shrink-0 text-sky-400" />}
            <span className="min-w-0">{reason.title}</span>
          </DialogTitle>
          <DialogDescription data-testid="text-wp-plan-reason">{reason.body}</DialogDescription>
        </DialogHeader>

        <dl className="space-y-3 rounded-xl border border-border bg-muted/30 p-3 sm:p-4" data-testid="wp-plan-rows">
          {!hasTitleRow && (
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Title</dt>
              <dd className="mt-0.5 break-words text-sm text-foreground">{articleTitle}</dd>
            </div>
          )}
          {rows.map((r) => (
            <div key={r.label}>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{r.label}</dt>
              <dd className={`mt-0.5 break-words text-sm ${r.tone === "bad" ? "text-red-300" : r.tone === "good" ? "text-emerald-300" : "text-foreground"}`}>
                {r.value}
              </dd>
            </div>
          ))}
          {loginMissing && (
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">WordPress login</dt>
              <dd className="mt-0.5 text-sm text-red-300">Not set up. Add it in Settings first.</dd>
            </div>
          )}
          {extra.map((line, i) => (
            <p key={i} className="break-words text-sm text-foreground/90">{line}</p>
          ))}
        </dl>

        {raw && (
          <details className="group text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none underline underline-offset-2">Technical details</summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted/40 p-3 text-[11px] leading-relaxed" data-testid="wp-plan-raw">
              {raw}
            </pre>
          </details>
        )}

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)} data-testid="button-close-wp-plan">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
